import React, { useEffect, useState } from "react";
import gettext from "../../shared/utils/gettext";
import cropr from "../../shared/utils/cropr";
import moment from "moment";
import "./Ceres.css";

import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";

type Props = {
  cropFieldDids: [string];
};

const SCENARIO_COLORS = ["#c87137", "#37c871", "#f83bd2", "#00aad4", "#fc2222", "#856fe3"];
const MEAN_COLOR = "#006600";
const HISTORICAL_COLOR = "#000000";

const GraphTooltip = ({ active, payload, label }: { active?: any; payload?: any; label?: any }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip bg-light p-2">
        <p className="label">{`${moment.unix(label).format("L")}`}</p>
        {payload.map(function (lineValue: any) {
          return (
            <div className="intro" style={{ color: lineValue.stroke }}>
              {lineValue.name}: {lineValue.value}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};
const CeresScenarioGrowthGraphImpl = (props: Props) => {
  let defaultCeresData: any[] = [];
  let defaultScenario: string[] = [];
  const [ceresData, setCeresData] = useState(defaultCeresData);
  const [scenarios, setScenarios] = useState(defaultScenario);
  const [error, setError] = useState("");

  useEffect(() => {
    cropr.ceres.apiClient.post({
      path: `crop_fields/cumulative/`,
      version: "V2",
      data: {
        id: props.cropFieldDids,
        until_date: cropr.partner_config?.["growth_advice"]?.["simulation_end_date"],
      },
      traditional: true,
      success: function (data: any) {
        const { graphData, scenarios } = ceresDataToChartsData(data);
        setCeresData(graphData);
        setScenarios(scenarios);
      },
      error: function () {
        if (props.cropFieldDids && props.cropFieldDids.length > 0) {
          setError(
            gettext(
              "Our excuses we could not display the estimated yield scenarios graph for some technical reason. Please contact us for further assistance"
            )
          );
        } else {
          setError(gettext("No crop fields available to display estimated yield scenarios"));
        }
      },
    });
  }, [props.cropFieldDids]);

  let colorIndex = 0;
  if (error) {
    return (
      <div className="row py-3 pb-5">
        <div className="col-12">
          <div className="card shadow-sm bg-white p-3">
            <h3>{gettext("Estimated yield scenarios")}</h3>
            <div className="col-12 border">{error}</div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="row py-3 pb-5">
        <div className="col-12">
          <div className="card shadow-sm bg-white p-3">
            <h3>
              {gettext("Estimated yield scenarios")}
              <span className="float-right">
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <BootstrapTooltip id="yield-graph-tooltip">
                      <p>
                        {gettext(`The graph shows the simulated yield for all crop fields. For 
                        the simulations until today the measured weather data is used. To predict 
                        the growth for the rest of the season historical weather data of the past 
                        years is used.`)}
                      </p>
                      <p>
                        {
                          // prettier-ignore
                          gettext(`The actual yield is the black line. The other lines are 
                            scenarios using the historical weather data of the past years. In the 
                            legend you can see which line is for which scenario. The thick green 
                            line is the mean yield of all scenarios.`)
                        }
                      </p>
                    </BootstrapTooltip>
                  }
                >
                  <button id="yield-graph-tooltip-trigger">
                    <i className="far fa-info-circle font-size-1 w-5 float-left " />
                  </button>
                </OverlayTrigger>
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                width={500}
                height={300}
                data={ceresData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                syncId="ceres-growth-scenario-graphs"
              >
                <XAxis dataKey="day" tickFormatter={(date) => moment.unix(date).format("L")} />
                <YAxis domain={[0, 100]} />
                <Legend />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<GraphTooltip />} />
                {scenarios.map((scenario) => {
                  let color;
                  if (!scenario) {
                    color = HISTORICAL_COLOR;
                  } else if (scenario === "MEAN") {
                    color = MEAN_COLOR;
                  } else {
                    color = SCENARIO_COLORS[colorIndex % SCENARIO_COLORS.length];
                    colorIndex++;
                  }
                  return (
                    <Line
                      key={scenario}
                      name={scenario ? scenario : "historical"}
                      dataKey={scenario}
                      stroke={color}
                      strokeWidth={scenario === "MEAN" ? 3 : 1}
                      connectNulls
                      dot={false}
                      type="monotone"
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }
};

const ceresDataToChartsData = (ceresData: any) => {
  let ceresCumulativeData = ceresData.cumulative_data;
  let graphData: any = [];
  let scenarios: string[] = [];
  Object.keys(ceresCumulativeData).forEach(function (day) {
    let timestamp = moment(day).unix();
    let dayGraphData = graphData.find(function (item: any) {
      return item.day === timestamp;
    });
    if (dayGraphData === undefined) {
      dayGraphData = { day: timestamp, date: day };
    }
    Object.keys(ceresCumulativeData[day]).forEach(function (scenario: any) {
      let scenarioDayData = ceresCumulativeData[day][scenario];
      let tuberMassAvg = cropr.l10n_utils.gr_per_m2_to_l10n_as_float(
        scenarioDayData.average_tuber_mass,
        1
      );
      if (!isNaN(tuberMassAvg)) {
        dayGraphData[scenario] = tuberMassAvg;
      }
      if (!scenarios.includes(scenario)) {
        scenarios.push(scenario);
      }
    });
    graphData.push(dayGraphData);
  });
  return { scenarios, graphData };
};

const CeresScenarioGrowthGraph = (props: Props) => {
  if (cropr.partner_config.advice_modules.includes("growth")) {
    return <CeresScenarioGrowthGraphImpl cropFieldDids={props.cropFieldDids} />;
  }
  return null;
};

export default CeresScenarioGrowthGraph;
